import { useDispatch, useSelector } from "react-redux";
import { API_BASE_URL, API_ENDPOINTS } from "../constants/api";
import {
	AuthStatus,
	Survey,
	User,
	USER_TYPE,
	UserEmailLoginInfo,
} from "../types/user";
import {
	DEFAULT_COUNTRY_CODE,
	EUR_COUNTRIES,
	EU_COUNTRIES,
	USA_COUNTRIES,
	US_COUNTRIES,
	MENA_COUNTRIES,
} from "../constants/locale";

import Http from "../services/http";
import { ApplicationState } from "../store";
import {
	actionSetGeoIpLoading,
	actionSetAuthenticated,
	actionSetGetDataUserLoading,
	actionSetRestriction,
	actionSetSACountry,
	actionSetUser,
	actionSetUserCountry,
	actionSetUserRegion,
	actionSetUserAnsweredSurvey,
	actionSetGetSurveyResponsePending,
	actionSetSubmitSurveyPending,
	actionSetUserEmailLoginInfo,
	actionSetUserSurveyHasError,
} from "../store/actions/user-actions";
import { UserState } from "../store/reducers/user-reducer";
import { UtilsLocale } from "../utils/UtilsLocale";
import { LocalStorage } from "../services/storage";
import {
	RESTRICTED,
	USER_DATA_NAMESPACE,
	USER_SESSION_ID,
} from "../constants/storage";
import { UtilsUser } from "../utils/UtilsUser";
import { generateToken } from "../utils/UtilsTokenGenerator";
import { useSiteSettingsHook } from "./site-settings-hooks";
import { IS_LOCALHOST } from "../constants/environment";
import { DETECTED } from "../constants/site-settings";
import { SystemState } from "../store/reducers/site-reducer";
import {
	DISABLED_FEATURES,
	useFeatureToggle,
} from "../utils/feature-toggle/useFeatureToggle";
import { BaseEntityRes } from "../types/base";
import { RCODES } from "../constants/rcodes";

export const useUserHooks = () => {
	const dispatch = useDispatch();
	const { getCountryCode } = useSiteSettingsHook();

	const { isDisabled } = useFeatureToggle();
	const { userCountryCode, userData, userRegion } = useSelector<
		ApplicationState,
		UserState
	>((state) => state.user);

	const isDigitalLegacy = Boolean(userData?.userType === USER_TYPE.DL);

	const siteSettings = useSelector<ApplicationState, SystemState>(
		(state) => state.system
	);

	const loadUserCountryCode = async () => {
		if (
			userCountryCode !== "" &&
			!isDisabled(DISABLED_FEATURES.PROFILE_AND_GEOIP_PERSIST)
		) {
			dispatch(actionSetGeoIpLoading(false));
			return;
		}

		try {
			dispatch(actionSetGeoIpLoading(true));

			const countryCode = IS_LOCALHOST
				? DEFAULT_COUNTRY_CODE
				: await getCountryCode();

			dispatch(actionSetUserCountry(countryCode));

			const isUsCountry: boolean = UtilsLocale.checkIfCountryIsFromRegion(
				countryCode,
				US_COUNTRIES
			);
			const isEuropeanCountry: boolean = UtilsLocale.checkIfCountryIsFromRegion(
				countryCode,
				EU_COUNTRIES
			);

			if (countryCode !== "") {
				dispatch(
					actionSetUserRegion({
						isFromUS: isUsCountry,
						isFromEurope: isEuropeanCountry,
					})
				);
			}

			dispatch(actionSetGeoIpLoading(false));
		} catch (e) {
			dispatch(actionSetGeoIpLoading(false));
			console.error(e);
		}
	};

	const getUserData = async () => {
		// Don't fetch if site settings haven't loaded yet
		if (
			!isDisabled(DISABLED_FEATURES.SITE_SETTINGS) &&
			siteSettings.sysProps.SA_COUNTRY === ""
		) {
			return;
		}

		// Skip if userData is already populated AND SA_COUNTRY won't change anything
		if (
			userData?.countryCode !== "" &&
			!isDisabled(DISABLED_FEATURES.PROFILE_AND_GEOIP_PERSIST) &&
			(isDisabled(DISABLED_FEATURES.SITE_SETTINGS) ||
				siteSettings.sysProps.SA_COUNTRY === "DETECTED")
		) {
			dispatch(actionSetGetDataUserLoading(false));
			return;
		}

		try {
			dispatch(actionSetGetDataUserLoading(true));

			const url = `${API_BASE_URL}${API_ENDPOINTS.PROFILE}`;
			const data = await Http.get(url);

			// Determine if user is email/guest login (not Samsung Account)
			const isEmailLogin = data.userType !== USER_TYPE.SA;

			const saAge =
				siteSettings.sysProps.SA_AGE !== DETECTED &&
				!isDisabled(DISABLED_FEATURES.SITE_SETTINGS) &&
				!isEmailLogin
					? Number(siteSettings.sysProps.SA_AGE)
					: undefined;

			const saCountry =
				siteSettings.sysProps.SA_COUNTRY !== DETECTED &&
				!isDisabled(DISABLED_FEATURES.SITE_SETTINGS) &&
				!isEmailLogin
					? UtilsLocale.convertISO2toISO3(siteSettings.sysProps.SA_COUNTRY)
					: (data.countryCode as string);

			const userData: User = {
				birthDate: data.birthday, // SA_AGE
				countryCode: saCountry, // SA_COUNTR
				joinedDate: data.joinedDate,
				loginId: data.loginId,
				loginIdType: data.loginIdType,
				mail: data.mail,
				name: data.name,
				validatedEmail: data.validatedEmail,
				profileImageUrl: data.profileImageUrl,
				userType: data.userType,
				userTypeCode: data.userTypeCode,
			};

			LocalStorage.set(USER_DATA_NAMESPACE, JSON.stringify(userData));

			const isSAFromEurope = UtilsLocale.checkIfCountryIsFromRegion(
				saCountry,
				EUR_COUNTRIES
			);

			const isSAFromMENA = UtilsLocale.checkIfCountryIsFromRegion(
				saCountry,
				MENA_COUNTRIES
			);

			const isRestricted = UtilsUser.isUserRestricted(
				saCountry,
				userData.birthDate,
				userData.userTypeCode,
				saAge,
				isSAFromEurope,
				isSAFromMENA
			);

			sessionStorage.setItem(RESTRICTED, String(isRestricted));
			const isSAFromAmerica = UtilsLocale.checkIfCountryIsFromRegion(
				saCountry,
				USA_COUNTRIES
			);

			const isSAUSA = saCountry === "USA";
			const authState = generateToken(12);
			LocalStorage.set(USER_SESSION_ID, authState);

			dispatch(actionSetUser(userData));
			dispatch(
				actionSetSACountry({
					isSAFromAmerica: isSAFromAmerica,
					isSAFromUSA: isSAUSA,
					isSAFromEurope: isSAFromEurope,
					isSAFromMENA: isSAFromMENA,
				})
			);
			dispatch(actionSetRestriction(isRestricted));
			dispatch(actionSetGetDataUserLoading(false));
			dispatch(actionSetAuthenticated(AuthStatus.Authenticated));

			return;
		} catch (e: any) {
			dispatch(actionSetGetDataUserLoading(false));
			dispatch(actionSetAuthenticated(AuthStatus.Unauthenticated));
			LocalStorage.remove(USER_DATA_NAMESPACE);
			return;
		}
	};

	/**
	 * Validates the current session by calling the Profile API.
	 * This is the new session validation entry point that replaces
	 * the old UtilsUser.isUserLoggedIn() cookie check.
	 *
	 * On success: dispatches user data + AuthStatus.Authenticated
	 * On failure: dispatches AuthStatus.Unauthenticated
	 */
	const validateSession = async () => {
		dispatch(actionSetAuthenticated(AuthStatus.Loading));
		try {
			const url = `${API_BASE_URL}${API_ENDPOINTS.PROFILE}`;
			const data = await Http.get(url);

			// Determine if user is email/guest login (not Samsung Account)
			const isEmailLogin = data.userType !== USER_TYPE.SA;

			const saAge =
				siteSettings.sysProps.SA_AGE !== DETECTED &&
				!isDisabled(DISABLED_FEATURES.SITE_SETTINGS) &&
				!isEmailLogin
					? Number(siteSettings.sysProps.SA_AGE)
					: undefined;

			const saCountry =
				siteSettings.sysProps.SA_COUNTRY !== DETECTED &&
				!isDisabled(DISABLED_FEATURES.SITE_SETTINGS) &&
				!isEmailLogin
					? UtilsLocale.convertISO2toISO3(siteSettings.sysProps.SA_COUNTRY)
					: (data.countryCode as string);

			const userData: User = {
				birthDate: data.birthday,
				countryCode: saCountry,
				joinedDate: data.joinedDate,
				loginId: data.loginId,
				loginIdType: data.loginIdType,
				mail: data.mail,
				name: data.name,
				validatedEmail: data.validatedEmail,
				profileImageUrl: data.profileImageUrl,
				userType: data.userType,
				userTypeCode: data.userTypeCode,
			};

			LocalStorage.set(USER_DATA_NAMESPACE, JSON.stringify(userData));

			const isSAFromEurope = UtilsLocale.checkIfCountryIsFromRegion(
				saCountry,
				EUR_COUNTRIES
			);

			const isSAFromMENA = UtilsLocale.checkIfCountryIsFromRegion(
				saCountry,
				MENA_COUNTRIES
			);

			const isRestricted = UtilsUser.isUserRestricted(
				saCountry,
				userData.birthDate,
				userData.userTypeCode,
				saAge,
				isSAFromEurope,
				isSAFromMENA
			);

			sessionStorage.setItem(RESTRICTED, String(isRestricted));
			const isSAFromAmerica = UtilsLocale.checkIfCountryIsFromRegion(
				saCountry,
				USA_COUNTRIES
			);

			const isSAUSA = saCountry === "USA";
			const authState = generateToken(12);
			LocalStorage.set(USER_SESSION_ID, authState);

			dispatch(actionSetUser(userData));
			dispatch(
				actionSetSACountry({
					isSAFromAmerica: isSAFromAmerica,
					isSAFromUSA: isSAUSA,
					isSAFromEurope: isSAFromEurope,
					isSAFromMENA: isSAFromMENA,
				})
			);
			dispatch(actionSetRestriction(isRestricted));
			dispatch(actionSetGetDataUserLoading(false));
			dispatch(actionSetAuthenticated(AuthStatus.Authenticated));
		} catch (e: any) {
			dispatch(actionSetGetDataUserLoading(false));
			dispatch(actionSetAuthenticated(AuthStatus.Unauthenticated));
			LocalStorage.remove(USER_DATA_NAMESPACE);
		}
	};

	const getUserSurveyResponse = async () => {
		try {
			dispatch(actionSetGetSurveyResponsePending(true));
			const url = `${API_BASE_URL}/${API_ENDPOINTS.SURVEY}`;

			const data = await Http.get(url);
			dispatch(actionSetUserAnsweredSurvey(data.answered));
			dispatch(actionSetGetSurveyResponsePending(false));
		} catch (e: any) {
			dispatch(actionSetGetDataUserLoading(false));
		}
	};

	const submitUserSurvey = async (formData: Survey) => {
		try {
			dispatch(actionSetSubmitSurveyPending(true));
			const url = `${API_BASE_URL}/${API_ENDPOINTS.SURVEY}`;

			const userSurveyData: Survey = {
				survey_id: 1,
				answer_1: formData.answer_1,
				answer_2: formData.answer_2,
				answer_3: formData.answer_3,
				answer_4: formData.answer_4,
				comments: formData.comments?.trim(),
			};

			const res: BaseEntityRes = await Http.post(url, userSurveyData);
			switch (res.rcode) {
				case RCODES.SUCCESS:
					dispatch(actionSetUserAnsweredSurvey(true));
					dispatch(actionSetSubmitSurveyPending(false));
					dispatch(actionSetUserSurveyHasError(false));
					break;
				default:
					dispatch(actionSetUserSurveyHasError(true));
					dispatch(actionSetSubmitSurveyPending(false));
					break;
			}
		} catch (e: any) {
			dispatch(actionSetGetDataUserLoading(false));
			dispatch(actionSetSubmitSurveyPending(false));
			dispatch(actionSetUserSurveyHasError(true));
		}
	};

	const setUserEmailLoginInfoData = (data: UserEmailLoginInfo) => {
		dispatch(actionSetUserEmailLoginInfo(data));
	};

	return {
		loadUserCountryCode,
		userRegion,
		userData,
		userCountryCode,
		getUserData,
		validateSession,
		isDigitalLegacy,
		submitUserSurvey,
		getUserSurveyResponse,
		setUserEmailLoginInfoData,
	};
};
